import { useCallback, useEffect, useRef, useState } from "react";
import SpotlightOverlay from "@/components/tour/SpotlightOverlay";
import { TOUR_GEOMETRY } from "@/constants/tour";
import { DESIGN_TOUR_STEPS } from "@/constants/tutorial";
import type { TourStep } from "@/interfaces/tour";
import type { TutorialModalProps } from "@/interfaces/tutorial";
import { waitForElements } from "@/utils/dom-helper";

const FIRST_TAB = DESIGN_TOUR_STEPS[0].canvasTab;

const STEPS: readonly TourStep[] = DESIGN_TOUR_STEPS.map((step, index) => ({
  id: step.targetId,
  badge: `STEP ${index + 1} OF ${DESIGN_TOUR_STEPS.length}`,
  title: step.title,
  body: step.desc,
  targets: [step.targetId],
  place: "below",
}));

/**
 * The design step's guided tour. Owns only what is specific to the builder — the step list and
 * the canvas tab each step needs; the overlay, geometry, keyboard and focus come from
 * `components/tour/SpotlightOverlay`, shared with the onboarding tour.
 */
export default function TutorialModal({
  open,
  onClose,
  onTabChange,
  onTourComplete,
}: TutorialModalProps) {
  const [index, setIndex] = useState(0);
  const [isSettling, setIsSettling] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  const step = DESIGN_TOUR_STEPS[index];
  const canvasTab = step?.canvasTab ?? FIRST_TAB;
  const targetId = step?.targetId;

  useEffect(() => {
    if (open) return;
    setIndex(0);
    setIsSettling(true);
  }, [open]);

  // Switch the canvas to this step's tab, then wait for its target before measuring — the tab's
  // content mounts a frame or two after the switch.
  useEffect(() => {
    if (!open || targetId === undefined) return;

    setIsSettling(true);
    onTabChange(canvasTab);

    const controller = new AbortController();
    abortRef.current = controller;

    void waitForElements([targetId], TOUR_GEOMETRY.targetTimeoutMs, controller.signal).then(() => {
      if (controller.signal.aborted) return;
      setIsSettling(false);
    });

    return () => controller.abort();
  }, [open, targetId, canvasTab, onTabChange]);

  const handleEnd = useCallback(
    (completed: boolean) => {
      abortRef.current?.abort();
      // Always land the user back on the email tab when the tour exits.
      onTabChange(FIRST_TAB);
      onClose();
      if (completed) onTourComplete?.();
    },
    [onTabChange, onClose, onTourComplete],
  );

  const handleNext = useCallback(() => {
    if (index < DESIGN_TOUR_STEPS.length - 1) {
      setIndex(index + 1);
      return;
    }
    handleEnd(true);
  }, [index, handleEnd]);

  const handleBack = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  const handleSkip = useCallback(() => handleEnd(false), [handleEnd]);

  return (
    <SpotlightOverlay
      open={open}
      steps={STEPS}
      index={index}
      isSettling={isSettling}
      onNext={handleNext}
      onBack={handleBack}
      onEnd={handleSkip}
    />
  );
}
